/**
 * Invoices Page - Claude.ai-inspired Design
 * List and manage invoices with filters and status tracking
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Filter,
  DollarSign,
  Calendar,
  User,
  Download,
  Send,
  Plus,
  TrendingUp,
  Eye,
} from 'lucide-react';
import { useToast } from '../components/ui';
import { TableListSkeleton } from '../components/ui/SkeletonLoaders';
import {
  useInvoices,
  useSendInvoice,
  useDownloadInvoicePDF,
  type Invoice,
} from '../hooks/useInvoices';

// Status Badge — supports all statuses including VIEWED, PARTIALLY_PAID, REFUNDED
type InvoiceStatus = Invoice['status'];

const StatusBadge = ({ status }: { status: InvoiceStatus }) => {
  const config: Record<InvoiceStatus, { label: string; className: string }> = {
    DRAFT: { label: 'Draft', className: 'claude-badge-default' },
    SENT: { label: 'Sent', className: 'claude-badge-warning' },
    VIEWED: { label: 'Viewed', className: 'claude-badge-warning' },
    PAID: { label: 'Paid', className: 'claude-badge-success' },
    PARTIALLY_PAID: { label: 'Partial', className: 'claude-badge-warning' },
    OVERDUE: { label: 'Overdue', className: 'claude-badge-error' },
    CANCELLED: { label: 'Cancelled', className: 'claude-badge-default' },
    REFUNDED: { label: 'Refunded', className: 'claude-badge-default' },
  };

  const { label, className } = config[status] || {
    label: status,
    className: 'claude-badge-default',
  };
  return <span className={`claude-badge ${className}`}>{label}</span>;
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error } = useInvoices({
    search: searchTerm,
    status: statusFilter,
  });

  const sendInvoice = useSendInvoice();
  const downloadPDF = useDownloadInvoicePDF();

  const handleSend = async (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    try {
      await sendInvoice.mutateAsync(invoiceId);
      showToast('success', 'Invoice sent successfully');
    } catch {
      showToast('error', 'Failed to send invoice');
    }
  };

  const handleDownloadPDF = async (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    try {
      await downloadPDF.mutateAsync(invoiceId);
      showToast('success', 'PDF downloaded');
    } catch {
      showToast('error', 'Failed to download PDF');
    }
  };

  const handleRowClick = (invoice: Invoice) => {
    if (invoice.status === 'DRAFT') {
      navigate(`/invoices/${invoice.id}/edit`);
    }
  };

  if (isLoading) {
    return <TableListSkeleton columns={6} rows={8} title="invoices" />;
  }

  if (error) {
    return (
      <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-3 text-destructive">
          <FileText className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Failed to load invoices</p>
            <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  const invoices = data?.invoices || [];

  // Client-side date range filtering
  const filteredInvoices = invoices.filter((invoice) => {
    if (dateFrom) {
      const invoiceDate = new Date(invoice.issueDate || invoice.createdAt);
      if (invoiceDate < new Date(dateFrom)) {
        return false;
      }
    }
    if (dateTo) {
      const invoiceDate = new Date(invoice.issueDate || invoice.createdAt);
      if (invoiceDate > new Date(`${dateTo}T23:59:59`)) {
        return false;
      }
    }
    return true;
  });

  // Calculate totals from invoices
  const totals = invoices.reduce(
    (acc, invoice) => {
      const amount = parseFloat(invoice.total);
      if (invoice.status === 'PAID') {
        acc.paid.count++;
        acc.paid.total += amount;
      } else if (invoice.status === 'SENT' || invoice.status === 'DRAFT') {
        acc.pending.count++;
        acc.pending.total += amount;
      } else if (invoice.status === 'OVERDUE') {
        acc.overdue.count++;
        acc.overdue.total += amount;
      }
      return acc;
    },
    {
      paid: { count: 0, total: 0 },
      pending: { count: 0, total: 0 },
      overdue: { count: 0, total: 0 },
    }
  );

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1>Invoices</h1>
          <p className="text-muted-foreground text-lg">Track and manage your invoicing</p>
        </div>
        <button
          onClick={() => navigate('/invoices/new')}
          className="claude-button claude-button-primary"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Paid
              </p>
              <p className="text-4xl font-semibold tracking-tight text-success">
                {formatCurrency(totals.paid.total)}
              </p>
              <p className="text-sm text-muted-foreground">
                {totals.paid.count} invoice{totals.paid.count !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success" />
            </div>
          </div>
        </div>

        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Pending
              </p>
              <p className="text-4xl font-semibold tracking-tight text-warning">
                {formatCurrency(totals.pending.total)}
              </p>
              <p className="text-sm text-muted-foreground">
                {totals.pending.count} invoice{totals.pending.count !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
              <FileText className="w-6 h-6 text-warning" />
            </div>
          </div>
        </div>

        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Overdue
              </p>
              <p className="text-4xl font-semibold tracking-tight text-destructive">
                {formatCurrency(totals.overdue.total)}
              </p>
              <p className="text-sm text-muted-foreground">
                {totals.overdue.count} invoice{totals.overdue.count !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-destructive" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="claude-card p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Search by invoice #, customer, or amount..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="claude-input pl-10 w-full"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`claude-button ${showFilters ? 'claude-button-primary' : 'claude-button-secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="pt-4 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="claude-input w-full"
                >
                  <option value="all">All Statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="SENT">Sent</option>
                  <option value="VIEWED">Viewed</option>
                  <option value="PAID">Paid</option>
                  <option value="PARTIALLY_PAID">Partially Paid</option>
                  <option value="OVERDUE">Overdue</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="claude-input w-full"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="claude-input w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoices Table */}
      <div className="claude-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="claude-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="claude-empty-state">
                      <FileText className="claude-empty-state-icon" />
                      <h3 className="claude-empty-state-title">No invoices found</h3>
                      <p className="claude-empty-state-description">
                        {searchTerm || statusFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : 'Create your first invoice to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredInvoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className={invoice.status === 'DRAFT' ? 'cursor-pointer hover:bg-muted/50' : ''}
                    onClick={() => handleRowClick(invoice)}
                  >
                    <td>
                      <span className="font-medium">{invoice.invoiceNumber}</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{invoice.customerName}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {invoice.customerEmail}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="font-semibold text-lg">
                        {formatCurrency(parseFloat(invoice.total))}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <span>
                            {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                          {invoice.viewedAt && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              Viewed{' '}
                              {new Date(invoice.viewedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <StatusBadge status={invoice.status} />
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => handleDownloadPDF(e, invoice.id)}
                          disabled={downloadPDF.isPending}
                          className="claude-button claude-button-ghost text-sm p-2"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {(invoice.status === 'DRAFT' || !invoice.sentAt) && (
                          <button
                            onClick={(e) => handleSend(e, invoice.id)}
                            disabled={sendInvoice.isPending}
                            className="claude-button claude-button-ghost text-sm p-2"
                            title="Send Invoice"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
